import { useTranslation, Trans } from "next-i18next";
import { useRouter } from "next/router";
import { Button, LoadingSpinner } from "@talladega/components";
import LocalImage from "@common/components/LocalImage";
import { ImageWithTranslation } from "@common/types/ImageWithTranslation";
import classNames from "classnames";
import { ArrowRightIcon } from "@talladega/icons";
import { DEFAULT_BACKGROUND_WIDTH } from "@modules/cms/constants/BackgroundImages";
import useVehicleInfoFromQuery from "@modules/query-string/hooks/useVehicleInfoFromQuery";
import { useCallback, useContext, useEffect, useState } from "react";
import useUser from "@modules/user/hooks/useUser";
import DealTypeEnum from "@common/enums/DealTypeEnum";
import { InstantCashOfferHeadingLayout } from "@modules/cms/types/InstantCashOfferHeadingLayout";
import {
  InstantCashOfferHeroEn,
  InstantCashOfferHeroFr,
} from "@public/images/instant-cash-offer";
import useAssociateTradeInMutation from "@modules/purchase/hooks/useAssociateTradeInMutation";
import { ICO_INQUIRY_EMAIL } from "@common/constants";
import {
  getStepById,
  icoCreateAccountStep,
  icoPersonalDetailsStep,
} from "@modules/purchase/types/PurchaseStep";
import useGetPurchasesByDealIds from "@modules/purchase/hooks/useGetPurchasesByDealIds";
import getUserDealIdsByType from "@modules/purchase/helpers/getUserDealIdsByType";
import HTTPErrorWithBody from "@modules/api/HTTPErrorWithBody";
import useUtmParamsFromQuery, {
  getSetUtmParams,
} from "@modules/query-string/hooks/useUtmParamsFromQuery";
import { gtmPushData, cloudflareImagesLoader } from "@talladega/helpers";
import { Event as HotjarEvent, hotjar } from "@modules/core/hotjar/types";
import useChangeUserTenantMutation, {
  ChangeUserTenantResponse,
} from "@modules/purchase/hooks/useChangeUserTenantMutation";
import LoginModalContext from "@modules/user/LoginModalContext";
import { removeQueryParams } from "@modules/core/routing/routingHelpers";
import useLogoutUserMutation from "@modules/user/hooks/useLogoutUserMutation";
import InstantCashOfferNotFoundModal from "./InstantCashOfferNotFoundModal";
import DealCreatedForOfferModal from "./DealCreatedForOfferModal";

type Props = {
  layout: InstantCashOfferHeadingLayout;
};

const vehicleDefaultImage: ImageWithTranslation = {
  srcEn: InstantCashOfferHeroEn,
  srcFr: InstantCashOfferHeroFr,
  altKey: "purchase:instant_cash_offer_header_image",
};

const InstantCashOfferHeading = ({ layout }: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const router = useRouter();
  const { locale, query } = router;
  const { background } = layout;
  const { year, make, model } = useVehicleInfoFromQuery();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isDealCreatedForOfferModalOpen, setIsDealCreatedForOfferModalOpen] =
    useState(false);

  const { user, isLoading, isFetching } = useUser();

  const dealIds = getUserDealIdsByType(user, DealTypeEnum.BuyIn);
  const {
    allPurchases,
    isLoading: isPurchasesLoading,
    isFetching: isPurchasesFetching,
  } = useGetPurchasesByDealIds(dealIds);
  const purchase = allPurchases[0];

  const {
    mutate: associateTradeInMutation,
    isPending: isAssociateTradeInMutationPending,
  } = useAssociateTradeInMutation();

  const logoutMutation = useLogoutUserMutation();

  const utmParams = useUtmParamsFromQuery();
  const changeUserTenantMutation = useChangeUserTenantMutation();
  const { setShowModal: setShowLoginModal } = useContext(LoginModalContext);

  const navigateToPurchaseStep = useCallback(() => {
    const currentStep =
      (purchase?.currentStepId
        ? getStepById("ico", purchase.currentStepId)
        : icoPersonalDetailsStep) ?? icoPersonalDetailsStep;
    router.push({
      pathname: currentStep.href,
      query: {
        dealId: purchase.id,
      },
    });
  }, [purchase?.currentStepId, purchase?.id, router]);

  const navigateToProfile = useCallback(() => {
    // if user has a deal and there is no trade-in token in URL, redirect to their ico offers
    router.push(
      {
        pathname: "/profile/my-offers",
        query: router.query,
      },
      "/profile/my-offers",
      {
        locale: "en",
      }
    );
  }, [router]);

  const associateOrGetExistingTrade = useCallback(() => {
    if (query.tradeIn && query.source) {
      removeQueryParams(router, ["transitionToThrottle"]);
      gtmPushData({
        event: "ICO",
        description: "View Your ICO",
      });

      // if trade-in token exists, always try to associate it with the user
      associateTradeInMutation(
        {
          tradeInToken: query.tradeIn as string,
          source: query.source as string,
        },
        {
          onSuccess: ({ dealId, currentStepId }) => {
            if (user?.isLoggedIn && currentStepId === icoCreateAccountStep.id) {
              router.push({
                pathname: "/offer-detail",
                query: {
                  dealId,
                  ...getSetUtmParams(utmParams),
                },
              });
            } else {
              router.push({
                pathname: "/confirm",
                query: {
                  dealId,
                  ...getSetUtmParams(utmParams),
                },
              });
            }
          },
          onError: (error) => {
            if (error instanceof HTTPErrorWithBody) {
              const err = error as HTTPErrorWithBody;

              if (
                err.response.status === 400 &&
                err.responseJson ===
                  "Trade-in already associated with deal by admin"
              ) {
                gtmPushData({
                  event: "ICO In Progress Modal",
                  description: "ICO In Progress Modal Open",
                  element: "ICO Landing Page",
                });
                hotjar.event(HotjarEvent.IcoInProgressModalOpen);

                setIsDealCreatedForOfferModalOpen(true);
              } else {
                setShowErrorModal(true);
              }
            }
          },
        }
      );
    } else if (allPurchases.length === 1 && purchase) {
      navigateToPurchaseStep();
    } else if (dealIds.length > 1) {
      navigateToProfile();
    } else if (!query.transitionToThrottle) {
      setShowErrorModal(true);
    }
  }, [
    allPurchases.length,
    associateTradeInMutation,
    dealIds.length,
    navigateToProfile,
    navigateToPurchaseStep,
    purchase,
    query.source,
    query.tradeIn,
    query.transitionToThrottle,
    router,
    user?.isLoggedIn,
    utmParams,
  ]);

  const changeUserTenant = useCallback(() => {
    if (query.transitionToThrottle) {
      changeUserTenantMutation.mutate(
        {
          tradeInToken: query.tradeIn as string,
          source: query.source as string,
          dealId: query.dealId as string,
        },
        {
          onSuccess: (response: ChangeUserTenantResponse) => {
            if (response.requiresLogin) {
              removeQueryParams(router, ["transitionToThrottle"]);
              setShowLoginModal(true);
            } else {
              associateOrGetExistingTrade();
            }
          },
        }
      );
    } else {
      associateOrGetExistingTrade();
    }
  }, [
    query.transitionToThrottle,
    query.tradeIn,
    query.source,
    query.dealId,
    changeUserTenantMutation,
    setShowLoginModal,
    router,
    associateOrGetExistingTrade,
  ]);

  const handleOnCtaClick = useCallback(() => {
    if (query.transitionToThrottle) {
      if (user?.isLoggedIn) {
        logoutMutation.mutate(undefined, {
          onSuccess: () => {
            changeUserTenant();
          },
        });
      } else {
        changeUserTenant();
      }
    } else {
      associateOrGetExistingTrade();
    }
  }, [
    associateOrGetExistingTrade,
    changeUserTenant,
    logoutMutation,
    query.transitionToThrottle,
    user?.isLoggedIn,
  ]);

  // if a deal id is provided and we switch tenants the query keys need to refresh for the purchases/user before we can navigate
  // to the correct step, maybe there is a better way to do this that a use effect
  useEffect(() => {
    if (
      query.transitionToThrottle &&
      query.dealId &&
      purchase &&
      purchase.id === query.dealId &&
      !isLoading &&
      !isFetching &&
      !isPurchasesLoading &&
      !isPurchasesFetching
    ) {
      removeQueryParams(router, ["transitionToThrottle"]);
      if (allPurchases.length === 1 && purchase) {
        navigateToPurchaseStep();
      } else if (dealIds.length > 1) {
        navigateToProfile();
      } else {
        setShowErrorModal(true);
      }
    }
  }, [
    allPurchases.length,
    dealIds.length,
    isFetching,
    isLoading,
    isPurchasesFetching,
    isPurchasesLoading,
    navigateToProfile,
    navigateToPurchaseStep,
    purchase,
    query.dealId,
    query.transitionToThrottle,
    router,
  ]);

  const baseDomain = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_DOMAIN || "";

  return (
    <>
      <div
        id="cms-financing-heading-section"
        className="bg-white overflow-visible no-scrollbar relative bg-cover bg-center"
        style={{
          backgroundColor:
            (background.option === "Color" && background.backgroundColor) ||
            "transparent",
          backgroundImage: `url(${cloudflareImagesLoader({
            src:
              (background.option === "Image" &&
                background.backgroundImage &&
                background.backgroundImage[0].sourceUrl) ||
              "",
            width: DEFAULT_BACKGROUND_WIDTH,
            baseDomain,
          })})`,
        }}
      >
        <div
          className={classNames(
            "relative max-w-page mx-auto flex items-center flex-col-reverse pt-10 pb-6 px-4 lg:px-5 lg:py-16 lg:flex-row-reverse"
          )}
        >
          <div className="flex-1 lg:basis-[45%] lg:px-0 md:px-28">
            <div className="w-full items-center justify-center text-center lg:text-left lg:items-start lg:justify-start">
              <p className="text-white font-semibold text-[3.25rem] leading-[3.625rem]">
                {year && make && model ? (
                  <Trans
                    t={t}
                    i18nKey="purchase:finalize_your_instant_cash_offer"
                    values={{
                      vehicle: `${year} ${make} ${model}`,
                    }}
                  />
                ) : (
                  t("purchase:you_are_almost_there")
                )}
              </p>
            </div>
            <div className="mt-6 w-full items-center justify-center text-center lg:text-left lg:items-start lg:justify-start text-white text-2xl">
              {t("purchase:securely_sell_your_vehicle_online")}
            </div>

            <div className="flex flex-col gap-2 mt-8 justify-center text-center md:items-center lg:text-start lg:items-start lg:justify-start">
              <Button
                rightIcon={<ArrowRightIcon />}
                onClick={handleOnCtaClick}
                buttonStyle="primary"
                fill="outline"
                spacing="tight-hug"
                disabled={
                  isLoading ||
                  isPurchasesLoading ||
                  isPurchasesFetching ||
                  isAssociateTradeInMutationPending
                }
              >
                {t("common:get_started")}
              </Button>
            </div>
          </div>
          <div className="flex-1 lg:basis-[55%] w-full relative">
            <div className="min-w-full h-auto relative inset-0">
              <div
                id="ico-vehicle-local-image"
                className="overflow-auto relative h-56 md:h-96 lg:mr-8"
              >
                <LocalImage
                  className="z-[2] w-full object-contain h-auto rounded-lg"
                  src={
                    locale === "fr"
                      ? vehicleDefaultImage.srcFr
                      : vehicleDefaultImage.srcEn
                  }
                  alt={t(vehicleDefaultImage.altKey)}
                  fill
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <InstantCashOfferNotFoundModal
        showErrorModal={showErrorModal}
        setShowErrorModal={setShowErrorModal}
        onPrimaryClick={() => {
          setShowErrorModal(false);
          router.push(`mailto:${ICO_INQUIRY_EMAIL}`);
        }}
        onSecondaryClick={() => setShowErrorModal(false)}
      />
      <DealCreatedForOfferModal
        isOpen={isDealCreatedForOfferModalOpen}
        onClose={() => setIsDealCreatedForOfferModalOpen(false)}
      />

      <LoadingSpinner
        isLoading={
          isLoading || isPurchasesLoading || isAssociateTradeInMutationPending
        }
        isOverlay
      />
    </>
  );
};

export default InstantCashOfferHeading;
